import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { useUser } from 'contexts/UserContext'

export default function useFetch() {
  const [, setLocation] = useLocation()
  const { user, setUser } = useUser()
  const [loading, setLoading] = useState(false)
  const [isCalled, setIsCalled] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  const API_URL = process.env.REACT_APP_API_URL

  // ✅ REF per tenir sempre l'accessToken més actual, evitant el problema de closure
  const accessTokenRef = useRef(user?.accessToken)
  useEffect(() => {
    accessTokenRef.current = user?.accessToken
  }, [user?.accessToken])

  const tryRefreshToken = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      })
      if (!res.ok) return false
      const payload = await res.json()
      if (!payload?.accessToken) return false

      // ✅ Actualitzem el ref immediatament, no esperem re-render
      accessTokenRef.current = payload.accessToken
      setUser((prev) => ({
        ...(prev || {}),
        accessToken: payload.accessToken
      }))
      return true
    } catch {
      return false
    }
  }, [API_URL, setUser])

  const doFetch = useCallback(
    async (params, { retryOn401 = true } = {}) => {
      const isAuthCall = params.url.startsWith('auth/')
      const url = `${API_URL}/${params.url}`
      const headers = params.formData
        ? {}
        : { 'Content-Type': 'application/json' }

      // ✅ Llegim del REF, no de la closure → sempre el token actual
      if (!isAuthCall && accessTokenRef.current) {
        headers.Authorization = `Bearer ${accessTokenRef.current}`
      }

      const body = params.formData
        ? params.formData
        : params.body !== undefined
          ? JSON.stringify(params.body)
          : undefined

      const response = await fetch(url, {
        method: params.method,
        headers,
        body,
        credentials: 'include'
      })

      if (response.ok) {
        const text = await response.text()
        const responseData = text ? JSON.parse(text) : null
        return { ok: true, status: response.status, data: responseData }
      }

      let errorData = null
      try {
        errorData = await response.json()
      } catch {
        // ignore
      }

      if (!isAuthCall && response.status === 401 && retryOn401) {
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          // ✅ El retry ara usarà el token nou gràcies al ref
          return doFetch(params, { retryOn401: false })
        }
        setUser(null)
        setLocation('/login')
        return { ok: false, status: 401, error: 'Autenticació fallida.' }
      }

      return {
        ok: false,
        status: response.status,
        error: errorData?.error || 'Error en la petició',
        detailedError: errorData?.detailedError
      }
    },
    // ✅ Ja NO depèn de user?.accessToken → elimina el problema de closure
    [API_URL, setUser, setLocation, tryRefreshToken]
  )

  const fetchAction = useCallback(
    async (params) => {
      setIsCalled(true)
      setData(null)
      setError(null)
      setMessage(null)
      setLoading(true)
      try {
        const result = await doFetch(params)
        if (result.ok) {
          setData(result.data)
          setMessage(result.data?.message || null)
        } else {
          setError(result.error)
          if (result.detailedError) {
            // console.log('useFetch() detailedError: ', result.detailedError)
          }
        }
      } catch (e) {
        setError(e?.message || String(e))
      } finally {
        setLoading(false)
      }
    },
    [doFetch]
  )

  return [{ loading, isCalled, data, error, message }, fetchAction]
}